import { c as _c } from "react/compiler-runtime";
import LogoOverlayButton from '@/app/[locale]/(worlds)/[roomId]/components/LogoOverlayButton';
import LogoOverlayRecommendationPanel from '@/app/[locale]/(worlds)/[roomId]/components/LogoOverlayRecommendationPanel';
import LogoOverlayRecommendations from '@/app/[locale]/(worlds)/[roomId]/components/LogoOverlayRecommendations';
import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe';
import { useUAContext } from '@/context/UAContext';
import { useWorldDetailsContext } from '@/context/WorldDetailsContext';
import { LOGO_OVERLAY_SCREEN_EDGE_MARGIN, LogoOverlayContentKind, type LogoOverlayContentModel } from '@/lib/logo-overlay';
import { cn } from '@/lib/utils';
import type { RecommendRoom } from '@/services/cms';
import { LogoPosition } from '@/services/cms';
import { motion } from 'framer-motion';
import { useSearchParams } from 'next/navigation';
import { Suspense, use, useEffect, useRef, useState } from 'react';
type ViverseLogoOverlayProps = {
  isVisible: boolean;
  position?: LogoPosition;
  isRotated?: boolean;
  /** hub_sid of the current room; when absent the overlay is suppressed. */
  roomId?: string;
  /**
   * Ref to the game container element.
   * When provided, the panel will not extend beyond the container's vertical bounds.
   * Pass this when the game viewport is smaller than the browser viewport (e.g. WorldIframe).
   */
  containerRef?: React.RefObject<HTMLElement | null>;
  className?: string;
};

// Button screen-edge margin comes from LOGO_OVERLAY_SCREEN_EDGE_MARGIN (lib/logo-overlay.ts).
// Left/right values are injected via inline style so a single constant update syncs everything.
type PositionLayout = {
  className: string;
  style: React.CSSProperties;
};
const positionMap: Record<string, PositionLayout> = {
  'top-left': {
    className: 'top-4',
    style: {
      left: LOGO_OVERLAY_SCREEN_EDGE_MARGIN
    }
  },
  'top-center': {
    className: 'top-4 left-1/2 -translate-x-1/2',
    style: {}
  },
  'top-right': {
    className: 'top-4',
    style: {
      right: LOGO_OVERLAY_SCREEN_EDGE_MARGIN
    }
  },
  'bottom-left': {
    className: 'bottom-4',
    style: {
      left: LOGO_OVERLAY_SCREEN_EDGE_MARGIN
    }
  },
  'bottom-center': {
    className: 'bottom-4 left-1/2 -translate-x-1/2',
    style: {}
  },
  'bottom-right': {
    className: 'bottom-4',
    style: {
      right: LOGO_OVERLAY_SCREEN_EDGE_MARGIN
    }
  }
};

// NOTE: 65px = (LOGO_WIDTH - LOGO_HEIGHT) / 2 = (154 - 24) / 2
// Corrects the positional shift caused by rotating the element 90°.
const rotatedPositionMap: Record<string, PositionLayout> = {
  'top-left': {
    className: 'top-4 translate-x-[65px] translate-y-[65px]',
    style: {
      right: LOGO_OVERLAY_SCREEN_EDGE_MARGIN
    }
  },
  'top-center': {
    className: 'top-1/2 translate-x-[65px] -translate-y-1/2',
    style: {
      right: LOGO_OVERLAY_SCREEN_EDGE_MARGIN
    }
  },
  'top-right': {
    className: 'bottom-4 translate-x-[65px] -translate-y-[65px]',
    style: {
      right: LOGO_OVERLAY_SCREEN_EDGE_MARGIN
    }
  },
  'bottom-left': {
    className: 'top-4 -translate-x-[65px] translate-y-[65px]',
    style: {
      left: LOGO_OVERLAY_SCREEN_EDGE_MARGIN
    }
  },
  'bottom-center': {
    className: 'top-1/2 left-4 -translate-x-[65px] -translate-y-1/2',
    style: {}
  },
  'bottom-right': {
    className: 'bottom-4 -translate-x-[65px] -translate-y-[65px]',
    style: {
      left: LOGO_OVERLAY_SCREEN_EDGE_MARGIN
    }
  }
};
function getDrawerAlignment(position: LogoPosition, isRotated: boolean): 'left' | 'right' | 'center' {
  if (isRotated) {
    return position.startsWith('top-') ? 'right' : 'left';
  }
  if (position.endsWith('-right')) return 'right';
  if (position.endsWith('-center')) return 'center';
  return 'left';
}
function LogoOverlayButtonWithRecommendation(t0) {
  const $ = _c(3);
  const {
    overlayModel,
    roomsPromise
  } = t0;
  const rooms = use(roomsPromise);
  let t1;
  if ($[0] !== overlayModel || $[1] !== rooms[0]) {
    t1 = <LogoOverlayButton overlayModel={overlayModel} firstRoom={rooms[0]} />;
    $[0] = overlayModel;
    $[1] = rooms[0];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function LogoOverlayRecommendationsWithRooms(t0) {
  const $ = _c(6);
  const {
    overlayModel,
    roomsPromise,
    cardOnly,
    showArrows,
    alignment
  } = t0;
  const rooms = use(roomsPromise);
  let t1;
  if ($[0] !== alignment || $[1] !== cardOnly || $[2] !== overlayModel || $[3] !== rooms || $[4] !== showArrows) {
    t1 = <LogoOverlayRecommendations overlayModel={overlayModel} rooms={rooms} cardOnly={cardOnly} showArrows={showArrows} alignment={alignment} />;
    $[0] = alignment;
    $[1] = cardOnly;
    $[2] = overlayModel;
    $[3] = rooms;
    $[4] = showArrows;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
const ViverseLogoOverlayContent = t0 => {
  const $ = _c(55);
  const {
    position: t1,
    isVisible,
    isRotated: t2,
    roomId,
    containerRef,
    className
  } = t0;
  const position = t1 === undefined ? "top-left" : t1;
  const isRotated = t2 === undefined ? false : t2;
  const {
    logoOverlayModel: overlayModel,
    logoOverlayRoomsPromise,
    requestLogoOverlayRecommendations
  } = useWorldDetailsContext();
  const {
    isMobile
  } = useUAContext();
  const searchParams = useSearchParams();
  let t3;
  if ($[0] !== searchParams) {
    t3 = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.FULL3D);
    $[0] = searchParams;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const isFull3D = t3;
  const isDesktopLayout = !isMobile && !isFull3D;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const buttonWrapperRef = useRef(null);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsDrawerOpen(false);
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== isVisible) {
    t5 = [isVisible];
    $[3] = isVisible;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[5] !== isVisible || $[6] !== overlayModel || $[7] !== requestLogoOverlayRecommendations || $[8] !== roomId) {
    t6 = () => {
      if (isVisible && roomId && overlayModel) {
        requestLogoOverlayRecommendations();
      }
    };
    t7 = [isVisible, roomId, overlayModel, requestLogoOverlayRecommendations];
    $[5] = isVisible;
    $[6] = overlayModel;
    $[7] = requestLogoOverlayRecommendations;
    $[8] = roomId;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  if (!isVisible || !roomId) {
    return null;
  }
  const {
    className: positionClassName,
    style: positionStyle
  } = isRotated ? rotatedPositionMap[position] ?? rotatedPositionMap["top-left"] : positionMap[position] ?? positionMap["top-left"];
  const rotateClass = isRotated ? "rotate-90" : "";
  const drawerPlacement = position.startsWith("bottom-") ? "above" : "below";
  let t8;
  if ($[11] !== isRotated || $[12] !== position) {
    t8 = getDrawerAlignment(position, isRotated);
    $[11] = isRotated;
    $[12] = position;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const drawerAlignment = t8;
  const showCloseButton = isDesktopLayout;
  const showArrows = isDesktopLayout;
  const cardOnly = !(isDesktopLayout && overlayModel?.contentKind === LogoOverlayContentKind.EXPERIENCE);
  if (!overlayModel) {
    let t10;
    let t9;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        opacity: 0
      };
      t10 = {
        opacity: 1
      };
      $[14] = t10;
      $[15] = t9;
    } else {
      t10 = $[14];
      t9 = $[15];
    }
    let t11;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1]
      };
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] !== className || $[18] !== positionClassName || $[19] !== rotateClass) {
      t12 = cn(`z-floating absolute ${rotateClass} ${positionClassName}`.trim(), className);
      $[17] = className;
      $[18] = positionClassName;
      $[19] = rotateClass;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    let t13;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <div className="w-fit"><LogoOverlayButton /></div>;
      $[21] = t13;
    } else {
      t13 = $[21];
    }
    let t14;
    if ($[22] !== positionStyle || $[23] !== t12) {
      t14 = <motion.div initial={t9} animate={t10} transition={t11} className={t12} style={positionStyle}>{t13}</motion.div>;
      $[22] = positionStyle;
      $[23] = t12;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    return t14;
  }
  let t10;
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 0
    };
    t10 = {
      opacity: 1
    };
    $[25] = t10;
    $[26] = t9;
  } else {
    t10 = $[25];
    t9 = $[26];
  }
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1]
    };
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== className || $[29] !== positionClassName || $[30] !== rotateClass) {
    t12 = cn(`z-floating absolute ${rotateClass} ${positionClassName}`.trim(), className);
    $[28] = className;
    $[29] = positionClassName;
    $[30] = rotateClass;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setIsDrawerOpen(_temp);
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== logoOverlayRoomsPromise || $[34] !== overlayModel) {
    t14 = <div ref={buttonWrapperRef} className="w-fit"><button type="button" onClick={t13} className="cursor-pointer">{logoOverlayRoomsPromise ? <Suspense fallback={<LogoOverlayButton overlayModel={overlayModel} />}><LogoOverlayButtonWithRecommendation overlayModel={overlayModel} roomsPromise={logoOverlayRoomsPromise} /></Suspense> : <LogoOverlayButton overlayModel={overlayModel} />}</button></div>;
    $[33] = logoOverlayRoomsPromise;
    $[34] = overlayModel;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setIsDrawerOpen(false);
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== cardOnly || $[38] !== drawerAlignment || $[39] !== logoOverlayRoomsPromise || $[40] !== overlayModel || $[41] !== showArrows) {
    t16 = logoOverlayRoomsPromise && <Suspense fallback={null}><LogoOverlayRecommendationsWithRooms overlayModel={overlayModel} roomsPromise={logoOverlayRoomsPromise} cardOnly={cardOnly} showArrows={showArrows} alignment={drawerAlignment} /></Suspense>;
    $[37] = cardOnly;
    $[38] = drawerAlignment;
    $[39] = logoOverlayRoomsPromise;
    $[40] = overlayModel;
    $[41] = showArrows;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== containerRef || $[44] !== drawerAlignment || $[45] !== drawerPlacement || $[46] !== isDrawerOpen || $[47] !== showCloseButton || $[48] !== t16) {
    t17 = <LogoOverlayRecommendationPanel isOpen={isDrawerOpen} placement={drawerPlacement} alignment={drawerAlignment} anchorRef={buttonWrapperRef} showCloseButton={showCloseButton} onCloseAction={t15} containerRef={containerRef}>{t16}</LogoOverlayRecommendationPanel>;
    $[43] = containerRef;
    $[44] = drawerAlignment;
    $[45] = drawerPlacement;
    $[46] = isDrawerOpen;
    $[47] = showCloseButton;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== positionStyle || $[51] !== t12 || $[52] !== t14 || $[53] !== t17) {
    t18 = <motion.div initial={t9} animate={t10} transition={t11} className={t12} style={positionStyle}>{t14}{t17}</motion.div>;
    $[50] = positionStyle;
    $[51] = t12;
    $[52] = t14;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  return t18;
};
const ViverseLogoOverlay = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={null}><ViverseLogoOverlayContent {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default ViverseLogoOverlay;
function _temp(prev) {
  return !prev;
}